import React, { useCallback, useContext, useRef, useEffect } from 'react';
import '../../../stylesheets/stylesLayout/tableViews.scss';
import FiltersBar from './FiltersBar';
import { TableBody, TableContainer, Table } from '@mui/material';
import TableHeader from './TableHeader';
import { getTableStructure } from './yourBookingsHelpers';
import { ContextYourBookings } from '../../contextsProperty/ContextYourBookings';
import TableRow from './BookingTableRow';
import { Alert, Typography } from '@mui/material';
import BookingStatisticsSection from './bookingStatistics/BookingStatisticsSection';
import ResultsIndicator from '../../formsProperty/Ari/ResultsIndicator';
import Card from '../../../muiComponents/Card';
import Button from '../../../muiComponents/Button';
import useTranslate from '../../../customHooks/useTranslate';

const YourBookings = () => {
    const {
        items,
        infoMessage,
        isLoading,
        setAreFiltersVisible,
        areFiltersVisible,
        visibleView,
        setVisibleView,
        page,
        totalResults,
        currentResults,
        searchNextPage,
    } = useContext(ContextYourBookings);
    const translate = useTranslate();

    const observer = useRef();
    const tableContainer = useRef();

    const tableStructure = getTableStructure();

    // para que la primera vez que se carga la lista, mientras hace la petición, muestre falsos elementos en la lista
    let dummyItems = [];
    for (let i = 0; i < 15; i++) {
        const dummyItem = { Id: i };
        dummyItems = [...dummyItems, dummyItem];
    }

    useEffect(() => {
        // para que al buscar, o al hacer scroll y es la página 1 o 0 (porque se han cambiado los filtros)
        // pongo el scroll arriba del todo
        if (visibleView === 'TABLE') {
            if (page <= 1) tableContainer.current.scrollTop = 0;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, visibleView]);

    const lastItem = useCallback(
        node => {
            if (isLoading) return;
            if (observer.current) observer.current.disconnect();
            observer.current = new IntersectionObserver(entries => {
                if (entries[0].isIntersecting) {
                    searchNextPage();
                }
            });
            if (node) observer.current.observe(node);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isLoading, page]
        // pongo la page en las dependecias, para que si cuando
        // se hace paginación infinita, si cambiar algún filtro coja la nueva página (es por el useEffect)
        // además si cambiar el idSupplierHotel, al ser un filtro, salta la validación
    );

    const isTableEmpty = !isLoading && !items.length;
    const isTableLoading = isLoading && !items.length;

    return (
        <div className='bookings__container'>
            <div className='page-title__container margin-bottom10'>
                <div style={{ display: 'flex', alignItems: 'baseline' }}>
                    <Typography variant='h1'>
                        {translate('bookings')}
                    </Typography>
                    {page > 0 && visibleView === 'TABLE' ? (
                        <ResultsIndicator
                            currentResults={currentResults}
                            totalResults={totalResults}
                            isLoading={isLoading}
                        />
                    ) : (
                        ''
                    )}
                </div>
                <div className='flex gap-10'>
                    {visibleView === 'TABLE' ? (
                        <>
                            <Button
                                onClick={() =>
                                    setAreFiltersVisible(
                                        prevState => !prevState
                                    )
                                }
                                startIcon={
                                    areFiltersVisible
                                        ? 'FilterAltOff'
                                        : 'FilterAlt'
                                }
                                color='secondary'
                            >
                                {translate(
                                    areFiltersVisible
                                        ? 'hide_filters'
                                        : 'show_filters'
                                )}
                            </Button>
                        </>
                    ) : (
                        ''
                    )}

                    <Button
                        onClick={() =>
                            setVisibleView(prev =>
                                prev === 'CHART' ? 'TABLE' : 'CHART'
                            )
                        }
                        startIcon={
                            visibleView === 'TABLE'
                                ? 'InsertChartOutlined'
                                : 'ListAlt'
                        }
                        color='secondary'
                    >
                        {translate(
                            visibleView === 'TABLE'
                                ? 'show_statistics'
                                : 'show_list'
                        )}
                    </Button>
                </div>
            </div>

            {visibleView === 'TABLE' ? (
                <>
                    <FiltersBar />

                    <Card
                        className={`table-container__bookings ${
                            items.length ? 'table__container ' : ''
                        }`}
                    >
                        <TableContainer ref={tableContainer}>
                            <Table stickyHeader size='small'>
                                <TableHeader columns={tableStructure.columns} />
                                <TableBody>
                                    {/* para que la primera vez que se carga la lista, mientras hace la petición, muestre falsos elementos en la lista */}
                                    {isTableLoading
                                        ? dummyItems.map(item => {
                                              return (
                                                  <TableRow
                                                      key={item.Id}
                                                      row={item}
                                                  />
                                              );
                                          })
                                        : items.map((item, index) => {
                                              if (items.length === index + 1) {
                                                  return (
                                                      <TableRow
                                                          componentRef={
                                                              lastItem
                                                          }
                                                          key={item.Id}
                                                          row={item}
                                                      />
                                                  );
                                              }
                                              return (
                                                  <TableRow
                                                      key={item.Id}
                                                      row={item}
                                                  />
                                              );
                                          })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {isTableEmpty && infoMessage.message && (
                            <Alert severity={infoMessage.type}>
                                {translate(infoMessage.message)}
                            </Alert>
                        )}
                    </Card>
                </>
            ) : (
                <>
                    <BookingStatisticsSection />
                </>
            )}
        </div>
    );
};

export default YourBookings;
